"use client";

import { useEffect, useRef, useState } from "react";
import {
  empty_prefill,
  event_options,
  package_options,
  party_size_options,
  type inquiry_prefill,
} from "@/lib/inquiry";

const field_class =
  "h-[52px] w-full rounded bg-surface-container-low px-4 font-body-md text-body-md font-normal text-on-surface outline-none transition-all placeholder:text-on-surface-variant/60 focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary-container";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="font-body-sm text-body-sm font-normal text-error">{message}</p>;
}

export function InquireForm({ prefill = empty_prefill }: { prefill?: inquiry_prefill }) {
  const banner_ref = useRef<HTMLDivElement>(null);
  const [email, set_email] = useState("");
  const [event_date, set_event_date] = useState(prefill.event_date);
  const [events, set_events] = useState<string[]>([]);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});
  const [first_name, set_first_name] = useState(prefill.first_name);
  const [form_error, set_form_error] = useState("");
  const [last_name, set_last_name] = useState(prefill.last_name);
  const [multi_day, set_multi_day] = useState(false);
  const [notes, set_notes] = useState(prefill.notes);
  const [package_value, set_package_value] = useState(prefill.package_value);
  const [party_size, set_party_size] = useState("1");
  const [phone, set_phone] = useState("");
  const [ready_by_time, set_ready_by_time] = useState("");
  const [status, set_status] = useState<"idle" | "sending" | "sent">("idle");
  const [venue, set_venue] = useState(prefill.venue);
  const [venue_tbd, set_venue_tbd] = useState(false);

  useEffect(() => {
    if (status === "sent") banner_ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [status]);

  function toggle_event(value: string) {
    set_events((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  }

  async function submit_inquiry() {
    set_status("sending");
    set_field_errors({});
    set_form_error("");

    const response = await fetch("/api/inquiries", {
      body: JSON.stringify({
        email,
        event_date,
        events,
        first_name,
        last_name,
        multi_day,
        notes,
        package_value,
        party_size,
        phone,
        ready_by_time,
        venue,
        venue_tbd,
      }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
    const payload = (await response.json()) as { error?: string; errors?: Record<string, string> };

    if (!response.ok) {
      set_status("idle");
      set_field_errors(payload.errors ?? {});
      set_form_error(payload.error || payload.errors?.form || "");
      return;
    }

    set_status("sent");
  }

  return (
    <form
      className="flex flex-col gap-8"
      id="inquiry-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit_inquiry();
      }}
    >
      {status === "sent" ? (
        <div className="rounded-xl bg-surface-container-high p-5 text-on-surface" ref={banner_ref}>
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-[24px] text-primary">verified</span>
            <div>
              <h3 className="font-title-lg text-title-lg font-semibold text-on-surface">Thank you for inquiring!</h3>
              <p className="mt-1 font-body-sm text-body-sm font-normal text-on-surface-variant">
                Your date request has been transmitted directly to Isha. Expect a bespoke quote and schedule review within 24 to 48 hours.
              </p>
            </div>
          </div>
        </div>
      ) : null}
      {form_error ? (
        <p className="font-body-sm text-body-sm font-normal text-error" role="alert">
          {form_error}
        </p>
      ) : null}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-surface-container pb-2">
          <span className="font-label-caps text-label-caps font-semibold uppercase tracking-widest text-primary">
            01 / Contact Information
          </span>
          <span className="font-body-sm text-body-sm font-normal text-on-surface-variant">* Required fields</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="first-name">
              First Name *
            </label>
            <input
              aria-invalid={Boolean(field_errors.first_name)}
              className={field_class}
              id="first-name"
              onChange={(event) => set_first_name(event.target.value)}
              placeholder="Priya"
              required
              type="text"
              value={first_name}
            />
            <FieldError message={field_errors.first_name} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="last-name">
              Last Name *
            </label>
            <input
              aria-invalid={Boolean(field_errors.last_name)}
              className={field_class}
              id="last-name"
              onChange={(event) => set_last_name(event.target.value)}
              placeholder="Shah"
              required
              type="text"
              value={last_name}
            />
            <FieldError message={field_errors.last_name} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="email">
              Email Address *
            </label>
            <input
              aria-invalid={Boolean(field_errors.email)}
              className={field_class}
              id="email"
              onChange={(event) => set_email(event.target.value)}
              placeholder="priya@example.com"
              required
              type="email"
              value={email}
            />
            <FieldError message={field_errors.email} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="phone">
              Phone Number *
            </label>
            <input
              aria-invalid={Boolean(field_errors.phone)}
              className={field_class}
              id="phone"
              onChange={(event) => set_phone(event.target.value)}
              placeholder="(512) 840-2911"
              required
              type="tel"
              value={phone}
            />
            <FieldError message={field_errors.phone} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-surface-container pb-2">
          <span className="font-label-caps text-label-caps font-semibold uppercase tracking-widest text-primary">
            02 / Primary Service Desired
          </span>
          <span className="font-body-sm text-body-sm font-normal text-on-surface-variant">Select main focus</span>
        </div>
        <div className="grid grid-cols-1 gap-2.5">
          {package_options.map((option) => (
            <label
              className="group relative flex cursor-pointer items-center justify-between rounded-xl bg-surface-container-low p-4 transition-all hover:bg-surface-container"
              key={option.value}
            >
              <span className="flex items-center gap-3">
                <input
                  checked={package_value === option.value}
                  className="h-4 w-4 accent-primary"
                  name="primary-service"
                  onChange={() => set_package_value(option.value)}
                  type="radio"
                  value={option.value}
                />
                <span>
                  <span className="block font-title-md text-title-md font-semibold text-on-surface">{option.label}</span>
                  <span className="block font-body-sm text-body-sm font-normal text-on-surface-variant">
                    {option.description}
                  </span>
                </span>
              </span>
              {option.badge ? (
                <span className="hidden rounded bg-surface-container-high px-2.5 py-1 font-label-caps text-label-caps font-semibold uppercase tracking-wider text-primary sm:inline-block">
                  {option.badge}
                </span>
              ) : null}
            </label>
          ))}
        </div>
        <FieldError message={field_errors.package_value} />
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-surface-container pb-2">
          <span className="font-label-caps text-label-caps font-semibold uppercase tracking-widest text-primary">
            03 / Celebration Dates & Schedule
          </span>
          <span className="font-body-sm text-body-sm font-normal text-on-surface-variant">Multi-event support</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="primary-date">
              Primary Event Date *
            </label>
            <input
              aria-invalid={Boolean(field_errors.event_date)}
              className={field_class}
              id="primary-date"
              onChange={(event) => set_event_date(event.target.value)}
              required
              type="date"
              value={event_date}
            />
            <FieldError message={field_errors.event_date} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="ready-time">
              Target Ready By Time (approx)
            </label>
            <input
              className={field_class}
              id="ready-time"
              onChange={(event) => set_ready_by_time(event.target.value)}
              type="time"
              value={ready_by_time}
            />
            <FieldError message={field_errors.ready_by_time} />
          </div>
        </div>
        <div className="flex flex-col gap-3 rounded-xl bg-surface-container-low p-4">
          <label className="flex cursor-pointer select-none items-center gap-3">
            <input
              checked={multi_day}
              className="h-4 w-4 accent-primary"
              onChange={(event) => set_multi_day(event.target.checked)}
              type="checkbox"
            />
            <span className="font-title-md text-title-md font-semibold text-on-surface">
              Multi-Day Wedding or Multi-Event Celebration?
            </span>
          </label>
          {multi_day ? (
            <div className="flex flex-col gap-2 border-t border-surface-container pt-2">
              <p className="mb-2 font-body-sm text-body-sm font-normal text-on-surface-variant">
                Select the events requiring hair, makeup, or draping coverage:
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {event_options.map((option) => (
                  <label className="flex cursor-pointer items-center gap-2 rounded bg-surface-container-lowest p-2" key={option.value}>
                    <input
                      checked={events.includes(option.value)}
                      className="accent-primary"
                      onChange={() => toggle_event(option.value)}
                      type="checkbox"
                    />
                    <span className="font-body-sm text-body-sm font-normal">{option.label}</span>
                  </label>
                ))}
              </div>
              <FieldError message={field_errors.events} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-surface-container pb-2">
          <span className="font-label-caps text-label-caps font-semibold uppercase tracking-widest text-primary">
            04 / Location & Scope
          </span>
          <span className="font-body-sm text-body-sm font-normal text-on-surface-variant">Bridal Suite Concierge</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="location">
              Venue Address or City *
            </label>
            <input
              aria-invalid={Boolean(field_errors.venue)}
              className={field_class}
              disabled={venue_tbd}
              id="location"
              onChange={(event) => set_venue(event.target.value)}
              placeholder="e.g. Camp Lucy, Dripping Springs"
              type="text"
              value={venue}
            />
            <label className="mt-1 flex cursor-pointer items-center gap-2">
              <input
                checked={venue_tbd}
                className="accent-primary"
                onChange={(event) => {
                  const checked = event.target.checked;
                  set_venue_tbd(checked);
                  set_venue(checked ? "Flexible / Venue TBD" : "");
                }}
                type="checkbox"
              />
              <span className="font-body-sm text-body-sm font-normal text-on-surface-variant">
                Venue still being finalized / TBD
              </span>
            </label>
            <FieldError message={field_errors.venue} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="party-count">
              Bridal Party Size
            </label>
            <select
              className={field_class}
              id="party-count"
              onChange={(event) => set_party_size(event.target.value)}
              value={party_size}
            >
              {party_size_options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <span className="mt-1 font-body-sm text-body-sm font-normal text-on-surface-variant/80">
              Assistant artists coordinated for large groups
            </span>
            <FieldError message={field_errors.party_size} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between border-b border-surface-container pb-2">
          <span className="font-label-caps text-label-caps font-semibold uppercase tracking-widest text-primary">
            05 / Vision & Special Notes
          </span>
          <span className="font-body-sm text-body-sm font-normal text-on-surface-variant">Optional</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="font-label-md text-label-md font-medium text-on-surface" htmlFor="notes">
            Tell us about your style vision, saree fabrics, or special requirements:
          </label>
          <textarea
            className="w-full resize-y rounded bg-surface-container-low p-4 font-body-md text-body-md font-normal text-on-surface outline-none transition-all placeholder:text-on-surface-variant/60 focus:bg-surface-container-lowest focus:ring-2 focus:ring-primary-container"
            id="notes"
            onChange={(event) => set_notes(event.target.value)}
            placeholder="Mention your bridal outfit colors, whether you desire traditional heavy lehenga dupatta placement, natural dewy glow vs. ultra-glam, or any skin sensitivities..."
            rows={4}
            value={notes}
          />
          <FieldError message={field_errors.notes} />
        </div>
      </div>
      <div className="flex flex-col items-center justify-between gap-4 pt-4 sm:flex-row">
        <button
          className="flex h-14 w-full items-center justify-center gap-3 rounded bg-primary-container px-8 font-title-md text-title-md font-semibold text-on-primary-container shadow-md transition-all duration-300 hover:bg-primary hover:text-on-primary hover:shadow-xl disabled:opacity-60 sm:w-auto"
          disabled={status === "sending"}
          type="submit"
        >
          <span>{status === "sending" ? "Sending Inquiry" : "Submit Availability Inquiry"}</span>
          <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
        </button>
        <div className="flex items-center gap-2 font-body-sm text-body-sm font-normal text-on-surface-variant">
          <span className="material-symbols-outlined text-[18px] text-primary">lock</span>
          <span>Zero obligations • Direct artist communication</span>
        </div>
      </div>
    </form>
  );
}
